import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  Bell,
  Search,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  LogOut,
  BarChart3,
  Building2,
  User,
  Shield,
  Layers,
  Key,
  BookOpen,
  HelpCircle,
  Activity,
  Cog,
  Bot
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import { HelpDrawer, HelpButton } from '../help';
import AIRiskManagerModal from '../AIRiskManagerModal';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [viewModeOpen, setViewModeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [systemMenuOpen, setSystemMenuOpen] = useState(false);
  const [aiRiskManagerOpen, setAiRiskManagerOpen] = useState(false);
  const [firmData, setFirmData] = useState(null);
  const [currentEntityContext, setCurrentEntityContext] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, viewMode, setViewMode } = useAuthStore();

  // Detect current entity context from URL for AI Risk Manager
  useEffect(() => {
    const path = location.pathname;

    // Match /requests/:id
    const requestMatch = path.match(/^\/requests\/([a-f0-9-]+)$/i);
    if (requestMatch) {
      setCurrentEntityContext({ type: 'request', id: requestMatch[1], name: 'Current Request' });
      return;
    }

    // Match /client-files/:id
    const clientMatch = path.match(/^\/client-files\/([a-f0-9-]+)$/i);
    if (clientMatch) {
      setCurrentEntityContext({ type: 'client', id: clientMatch[1], name: 'Current Client' });
      return;
    }

    // Match /companies/:id or /companies/:slug
    const companyMatch = path.match(/^\/companies\/([^/]+)$/i);
    if (companyMatch) {
      setCurrentEntityContext({ type: 'company', id: companyMatch[1], name: 'Current Company' });
      return;
    }

    // Match /kyb-sessions/:id
    const kybMatch = path.match(/^\/kyb-sessions\/([a-f0-9-]+)$/i);
    if (kybMatch) {
      setCurrentEntityContext({ type: 'application', id: kybMatch[1], name: 'Current KYB Application' });
      return;
    }

    // No entity context on this page
    setCurrentEntityContext(null);
  }, [location.pathname]);

  useEffect(() => {
    // Only fetch firm data for firm users, not platform admins
    if (user && user.userType === 'FIRM_USER' && user.company) {
      fetchFirmData();
    }
  }, [user]);

  const fetchFirmData = async () => {
    try {
      // For firm users, get their firm data from the user object
      if (user?.company?.slug) {
        const response = await api.get(`/firms/${user.company.slug}`);
        setFirmData(response.data.firm);
      }
    } catch (error) {
      console.error('Failed to fetch firm data:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setViewModeOpen(false);

    // Force a full page reload to reset all component state
    // This ensures the user gets a complete experience change
    if (mode === 'end_user') {
      window.location.href = '/my-verification';
    } else {
      window.location.href = '/dashboard';
    }
  };

  const viewModeOptions = [
    { id: 'platform_admin', label: 'Platform Admin', description: 'Manage all companies and platform' },
    { id: 'company_admin', label: 'Company Admin', description: 'Manage company and clients' },
    { id: 'end_user', label: 'Client View', description: 'End user verification flow' }
  ];

  const currentViewMode = viewModeOptions.find(v => v.id === viewMode) || viewModeOptions[0];

  // Get current page ID for help context
  const getCurrentPageId = () => {
    const path = location.pathname;

    // Client view pages
    if (path === '/my-verification') return 'myVerification';
    if (path === '/my-documents') return 'myDocuments';
    if (path === '/my-profile') return 'myProfile';

    // Admin/Company pages
    if (path === '/dashboard') return 'dashboard';
    if (path === '/api') return 'api';
    if (path === '/clients') return 'clients';
    if (path === '/verification-sources') return 'verificationSources';
    if (path === '/platform-settings') return 'platformSettings';
    if (path === '/reports') return 'reports';
    if (path.startsWith('/kyb-sessions')) return 'kybSessions';

    return 'default';
  };

  // Navigation items based on view mode
  const getNavigation = () => {
    if (viewMode === 'platform_admin') {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Companies', href: '/companies', icon: Building2 },
        { name: 'All Clients', href: '/all-clients', icon: Users },
        { name: 'Requests', href: '/requests', icon: FileText },
        { name: 'Users', href: '/users', icon: Users },
        { name: 'Verification Sources', href: '/verification-sources', icon: Shield },
        { name: 'KYC Levels', href: '/verification-levels', icon: Layers },
        { name: 'KYB Levels', href: '/kyb-verification-levels', icon: Building2 },
        { name: 'KYB Sessions', href: '/kyb-sessions', icon: Shield },
        { name: 'Monitoring', href: '/monitoring', icon: Activity },
      ];
    } else if (viewMode === 'company_admin') {
      const nav = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Clients', href: '/clients', icon: Users },
        { name: 'Requests', href: '/requests', icon: FileText },
        { name: 'Reports', href: '/reports', icon: BarChart3 },
      ];

      // Add API menu item if API is enabled for this firm
      if (firmData?.api_enabled) {
        nav.push({ name: 'API', href: '/api', icon: Key });
      }

      nav.push({ name: 'Settings', href: '/settings', icon: Settings });

      return nav;
    } else if (viewMode === 'end_user') {
      return [
        { name: 'My Verification', href: '/my-verification', icon: FileText },
        { name: 'Documents', href: '/my-documents', icon: FileText },
        { name: 'Profile', href: '/my-profile', icon: User },
      ];
    }
    // Default fallback
    return [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Clients', href: '/clients', icon: Users },
      { name: 'Requests', href: '/requests', icon: FileText },
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Settings', href: '/settings', icon: Settings },
    ];
  };

  const navigation = getNavigation();

  // System menu items for platform admin
  const systemMenuItems = [
    { name: 'Logs', href: '/audit-logs', icon: FileText },
    { name: 'API Tokens', href: '/platform-api-tokens', icon: Key },
    { name: 'Platform Settings', href: '/platform-settings', icon: Settings },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Check if any system menu item is active
  const isSystemMenuActive = systemMenuItems.some(item => isActive(item.href));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-navy-900 text-white transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-navy-800 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <span className="text-xl font-semibold">Acquiva</span>
          </Link>
          <button
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto flex-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
                  isActive(item.href)
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-navy-800 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.name}</span>
              </Link>
            );
          })}

          {/* System Menu - Collapsible (Platform Admin only) */}
          {viewMode === 'platform_admin' && (
            <div className="pt-2">
              <button
                onClick={() => setSystemMenuOpen(!systemMenuOpen)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
                  isSystemMenuActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-navy-800 hover:text-white'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Cog className="w-5 h-5" />
                  <span className="font-medium text-sm">System</span>
                </div>
                {systemMenuOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {systemMenuOpen && (
                <div className="mt-1 ml-4 space-y-1">
                  {systemMenuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                          isActive(item.href)
                            ? 'bg-blue-500 text-white'
                            : 'text-gray-400 hover:bg-navy-800 hover:text-white'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Help Section - Context-aware based on view mode */}
          <div className="pt-4 mt-4 border-t border-navy-800">
            <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Help & Support
            </p>

            {/* Client View - Only show user help */}
            {viewMode === 'end_user' && (
              <button
                onClick={() => setHelpOpen(true)}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
                <span className="font-medium">Help</span>
              </button>
            )}

            {/* Company Admin View - Show company help and admin guide */}
            {viewMode === 'company_admin' && (
              <>
                <button
                  onClick={() => setHelpOpen(true)}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="font-medium">Help</span>
                </button>
                <Link
                  to="/docs/admin-guide"
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  <BookOpen className="w-5 h-5" />
                  <span className="font-medium">Admin Guide</span>
                </Link>
              </>
            )}

            {/* Platform Admin View - Show everything including technical docs */}
            {viewMode === 'platform_admin' && (
              <>
                <button
                  onClick={() => setHelpOpen(true)}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="font-medium">Help</span>
                </button>
                <Link
                  to="/docs/admin-guide"
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  <BookOpen className="w-5 h-5" />
                  <span className="font-medium">Admin Guide</span>
                </Link>
                <Link
                  to="/docs/technical"
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-navy-800 hover:text-white transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Technical Docs</span>
                </Link>
              </>
            )}
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              className="lg:hidden text-gray-500 hover:text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 max-w-lg mx-4 lg:mx-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients, requests..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* View Mode Switcher - Only for Platform Admins */}
              {user?.userType === 'PLATFORM_ADMIN' && (
                <div className="relative">
                  <button
                    className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm"
                    onClick={() => setViewModeOpen(!viewModeOpen)}
                  >
                    <span className="text-sm font-medium">{currentViewMode.label}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {viewModeOpen && (
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Switch View Mode</p>
                      </div>
                      {viewModeOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleViewModeChange(option.id)}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            viewMode === option.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-sm font-medium ${
                                viewMode === option.id ? 'text-blue-600' : 'text-gray-900'
                              }`}>
                                {option.label}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                            </div>
                            {viewMode === option.id && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button className="relative p-2 text-gray-400 hover:text-gray-500">
                <Bell className="w-6 h-6" />
              </button>

              <div className="relative">
                <button
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
                  onClick={() => setProfileOpen(!profileOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                    <Link
                      to="/settings"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setProfileOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </Link>
                    <button
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 relative">
          {/* AI Risk Manager Button - Top right of main content */}
          {(viewMode === 'platform_admin' || viewMode === 'company_admin') && (
            <button
              onClick={() => setAiRiskManagerOpen(true)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 z-20 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 group"
              aria-label="AI Risk Manager"
              title="AI Risk Manager"
            >
              <Bot className="w-5 h-5" />
              <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                AI Risk Manager
              </span>
            </button>
          )}
          <Outlet />
        </main>
      </div>

      {/* Help System */}
      <HelpButton onClick={() => setHelpOpen(true)} />
      <HelpDrawer
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        pageId={getCurrentPageId()}
        viewMode={viewMode}
      />

      {/* AI Risk Manager Modal */}
      {(viewMode === 'platform_admin' || viewMode === 'company_admin') && (
        <AIRiskManagerModal
          isOpen={aiRiskManagerOpen}
          onClose={() => setAiRiskManagerOpen(false)}
          currentContext={currentEntityContext}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
